import * as THREE from "three"
import React, { useEffect, useRef, useMemo } from "react"
import { useFrame } from "react-three-fiber"
import lerp from "lerp"
import { getMouseDegrees } from "../../utils"
import { SkeletonUtils } from "three/examples/jsm/utils/SkeletonUtils"

function moveJoint(mouse, joint, degreeLimit = 40) {
  const degrees = getMouseDegrees(mouse.current.x, mouse.current.y, degreeLimit)
  joint.rotation.xD = lerp(joint.rotation.xD || 0, degrees.y, 0.1)
  joint.rotation.yD = lerp(joint.rotation.yD || 0, degrees.x, 0.1)
  joint.rotation.x = THREE.Math.degToRad(joint.rotation.xD)
  joint.rotation.y = THREE.Math.degToRad(joint.rotation.yD)
}

function playModifierAnimation(from, fSpeed, to, tSpeed, isAnimating) {
  try {
    isAnimating.current = true
    to.setLoop(THREE.LoopOnce)
    to.reset()
    to.play()
    from.crossFadeTo(to, fSpeed, true)
    setTimeout(function() {
      from.enabled = true
      to.crossFadeTo(from, tSpeed, true)
      isAnimating.current = false
    }, to._clip.duration * 1000 - (tSpeed + fSpeed) * 1000)
  } catch (e) {
    console.error(e)
    isAnimating.current = false
  }
}

export function ModelRenderer({
  animations,
  mouse,
  showModel,
  scene: modelSceneObject,
  nodes: node,
  texture,
  ...props
}) {
  const group = useRef()
  const { nodes } = useMemo(() => {
    let nodes = {},
      materials = {}
    const model = SkeletonUtils.clone(modelSceneObject)
    model.traverse(object => {
      if (object.name) {
        nodes = { ...nodes, [object.name]: object }
      }
      if (object.material && !materials[object.material.name]) {
        materials[object.material.name] = object.material
      }
    })

    return { modelScene: model, nodes, materials }
  }, [])

  const mixer = useMemo(() => new THREE.AnimationMixer(), [])
  const isAnimating = useRef(false)
  const actions = useRef()

  useEffect(() => {
    actions.current = {
      idle: mixer.clipAction(
        THREE.AnimationClip.findByName(animations, "idle"),
        group.current
      ),
      wave: mixer.clipAction(
        THREE.AnimationClip.findByName(animations, "wave"),
        group.current
      ),
    }
    actions.current.idle.play()
    return () => {
      animations.forEach(clip => mixer.uncacheClip(clip))
    }
  }, [])

  useFrame((state, delta) => {
    mixer.update(delta)
    if (mouse.current.x !== undefined && mouse.current.y !== undefined) {
      moveJoint(mouse, nodes.mixamorigNeck)
      moveJoint(mouse, nodes.mixamorigSpine)
    }
  })

  useEffect(() => {
    if (showModel && !isAnimating.current) {
      playModifierAnimation(
        actions.current.idle,
        0.25,
        actions.current.wave,
        0.25,
        isAnimating
      )
    }
  }, [showModel])

  return (
    <group ref={group} dispose={null}>
      <group
        position={[0, -2, 2]}
        rotation={[Math.PI / 2, 0, 0]}
        scale={[0.0025, 0.0025, 0.0025]}
      >
        <primitive object={nodes["mixamorigHips"]} />
        <skinnedMesh
          geometry={nodes["Neeraj"].geometry}
          skeleton={nodes["Neeraj"].skeleton}
          rotation={[-Math.PI / 2, 0, 0]}
          onClick={() => {
            if (!isAnimating.current) {
              playModifierAnimation(
                actions.current.idle,
                0.25,
                actions.current.wave,
                0.25,
                isAnimating
              )
            }
          }}
          scale={[100, 100, 100]}
        >
          <meshPhongMaterial
            attach="material"
            map={texture}
            map-flipY={false}
            skinning
          />
        </skinnedMesh>
      </group>
    </group>
  )

  // return <primitive object={modelScene} />
}
